function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, limit) {
  let lastFunc;
  let lastRan;
  return () => {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      fn.apply(context, args);
      lastRan = Date.now()
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          fn.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan))
    }
  }
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
  };
}

// grab an element by id, set the value of a given property.
function updateHeightVariable(propertyName, newValue) {
  const currentValue = document.body.style.getPropertyValue(propertyName);
  if (currentValue === newValue) return;
  document.body.style.setProperty(propertyName, newValue);
  if (propertyName === '--header-height') {
    const parsedHeight = parseFloat(newValue) || 0;
    if (window.theme && window.theme.header) {
      window.theme.header.height = parsedHeight;
    }
    document.dispatchEvent(new CustomEvent('header-height:change', {
      detail: { height: parsedHeight }
    }));
  }
}

function setElementHeightVariable(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;
  const height = `${element.offsetHeight}px`;
  updateHeightVariable(propertyName, height);
}

function updateAllHeightVariables() {
  setElementHeightVariable('site-header', '--header-height');
  setElementHeightVariable('site-footer', '--footer-height');
}

/**
 * ResizeObserver-based height tracking for dynamic elements.
 * More efficient than scroll-based polling - only fires when dimensions actually change.
 * Automatically handles:
 * - App banner collapse/expand on scroll (CSS Grid changes height)
 * - Window resize
 * - Dynamic content changes
 * - Font loading affecting layout
 */
const heightObserverCache = new Map();

function observeElementHeight(elementId, propertyName) {
  const element = document.getElementById(elementId);
  if (!element) return;

  // Initial measurement
  setElementHeightVariable(elementId, propertyName);

  // Create ResizeObserver for this element
  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      // Use borderBoxSize for accurate height including padding/border
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight;
      const newValue = `${height}px`;

      // Only update if value changed (avoids unnecessary style recalcs)
      updateHeightVariable(propertyName, newValue);
    }
  });

  observer.observe(element);
  heightObserverCache.set(elementId, observer);

  return observer;
}

// Initialize height observers after DOM is ready
// Use requestIdleCallback for non-blocking initialization, fallback to setTimeout
const initHeightObservers = () => {
  observeElementHeight('site-header', '--header-height');
  observeElementHeight('site-footer', '--footer-height');
};

if ('requestIdleCallback' in window) {
  requestIdleCallback(initHeightObservers, { timeout: 1000 });
} else {
  setTimeout(initHeightObservers, 500);
}

// Keep window resize listener as fallback for elements not observed
// and for any other components that might depend on resize events
const debouncedUpdateHeightVariables = debounce(updateAllHeightVariables, 250);
window.addEventListener('resize', debouncedUpdateHeightVariables);

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function() {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function(event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch(e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ['ARROWUP', 'ARROWDOWN', 'ARROWLEFT', 'ARROWRIGHT', 'TAB', 'ENTER', 'SPACE', 'ESCAPE', 'HOME', 'END', 'PAGEUP', 'PAGEDOWN']
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if(navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

    if (mouseClick) return;

    currentFocusedElement = document.activeElement;
    currentFocusedElement.classList.add('focused');

  }, true);
}

function pauseAllMedia() {
  // Use specific origins for security instead of '*'
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', 'https://www.youtube.com');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', 'https://player.vimeo.com');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true })

    this.querySelectorAll('button').forEach(
      (button) => button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}

customElements.define('quantity-input', QuantityInput);

/*
 * Shopify Common JS
 *
 */
if ((typeof window.Shopify) == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function(fn, scope) {
  return function() {
    return fn.apply(scope, arguments);
  }
};

Shopify.setSelectorByValue = function(selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function(target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on'+eventName, callback);
};

Shopify.postLink = function(path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for(var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function(country_domid, province_domid, options) {
  this.countryEl         = document.getElementById(country_domid);
  this.provinceEl        = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler,this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function() {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function() {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function(e) {
    var opt       = this.countryEl.options[this.countryEl.selectedIndex];
    var raw       = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function(selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function(selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  }
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    if (navigator.platform === 'iPhone') document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
  }

  onKeyUp(event) {
    if(event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if(!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary')) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if(isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach(details =>  {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle?.querySelector('summary')?.setAttribute('aria-expanded', false);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);
  }

  onFocusOut(event) {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    }

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.getElementById('shopify-section-header');
    this.bar = this.bar || document.querySelector('announcement-bar');
    this.borderOffset = this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    if (this.bar) {
      document.documentElement.style.setProperty('--header-bottom-position', `${parseInt(this.bar.getBoundingClientRect().bottom - this.borderOffset, 10)}px`);
    }
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    scrollLock.enabledBodyScrollLock();
  }

  closeMenuDrawer(event, elementToFocus) {
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    scrollLock.disableBodyScrollLock();
  }
}

customElements.define('header-drawer', HeaderDrawer);

/*
 * ModalDialog - Custom element for modal dialogs
 *
 * THIS COMPONENT SHOULD BE DEPRECATED OVER TIME AS WE ADAPT NATIVE HTML DIALOG AND POPOVER FEATURES
 *
 * This component handles the modal dialog functionality for the theme.
 *
 * @class ModalDialog
 * @extends HTMLElement
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.querySelector('.modal-overlay')?.addEventListener(
      'click',
      this.hide.bind(this)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code?.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;

    if(this.classList.contains('quick-add-modal')) {
      document.querySelector('#main-content').appendChild(this);
    } else {
      document.body.appendChild(this);
    }

    this.addEventListener('click', (event) => {
      if (event.target.nodeName === 'MODAL-DIALOG') this.hide();
    });
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.style.overflow = 'hidden';
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
    // document.body.classList.add('hide-scroll');
    // If the modal opening contains a bundle form, we want to generate the bundle id and attach it.
    const bundleForm = this.querySelector('welcome-package-form');
    if(bundleForm){
      // our best attempt to get a good 'random' number for the bundle id
      let bundleId = Math.floor(Math.random() * Math.floor(Math.random() * Date.now()));
      bundleForm.dataset.bundleId = `bundle-${bundleId}`;
    }
  }

  hide() {
    document.body.style.overflow = 'auto';
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
    const backInStockForm = this.querySelector('.js-back-in-stock');
    if(backInStockForm){
      backInStockForm.classList.remove('product__stock--success');
      backInStockForm.classList.remove('product__stock--error');
    }
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
 * ModalOpener - Custom element for opening modal dialogs
 *
 * THIS COMPONENT SHOULD BE DEPRECATED OVER TIME AS WE ADAPT NATIVE HTML DIALOG AND POPOVER FEATURES
 *
 * This component handles opening modal dialogs either through an internal button element
 * or by clicking on the component itself when data-self-open="true" is set.
 *
 *
 * @example
 * <!-- Standard usage with button -->
 * <modal-opener data-modal="#my-modal">
 *   <button>Open Modal</button>
 * </modal-opener>
 *
 * @example
 * <!-- Self-opening usage -->
 * <modal-opener data-modal="#my-modal" data-self-open="true">
 *   Click me to open modal
 * </modal-opener>
 *
 * @class ModalOpener
 * @extends HTMLElement
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();
    this.boundHandleModalOpen = this.handleModalOpen.bind(this);
    this.initialized = false;
  }

  connectedCallback() {
    // Defer initialization to allow innerHTML to be set after createElement
    // Use requestAnimationFrame to ensure DOM is fully ready
    requestAnimationFrame(() => this.initialize());
  }

  initialize() {
    if (this.initialized) return;
    this.initialized = true;

    // Check if this component should open modals when clicked directly
    const isSelfOpening = this.dataset.selfOpen === 'true';

    if (isSelfOpening) {
      // Register click event on the component itself
      this.addEventListener('click', this.boundHandleModalOpen);
    } else {
      // Look for a button element to handle clicks (existing behavior)
      const button = this.querySelector('button');

      if (!button) return;
      button.addEventListener('click', this.boundHandleModalOpen);
    }
  }

  /**
   * Handles the modal opening logic
   * @param {Event} event - The click event
   * @private
   */
  handleModalOpen(event) {
    const dataModal = this.getAttribute('data-modal');
    const modal = document.querySelector(dataModal);

    if (modal) {
      // For self-opening modals, pass the component itself as the opener
      // For button-based modals, pass the button element
      const opener = this.dataset.selfOpen === 'true' ? this : event.currentTarget;

      modal.show(opener);

      // Special handling for size modals
      if (dataModal.includes("size")) {
        const sizeContainer = document.querySelector(".product__size-inner");
        if (sizeContainer && sizeContainer.scrollWidth <= sizeContainer.clientWidth) {
          const slideOption = document.querySelector(".slide-option");
          if (slideOption) {
            slideOption.style.display = "none";
          }
        }
      }
    }
  }
}
customElements.define('modal-opener', ModalOpener);

// Global events for video management
const EVENTS = {
  MEDIA_STARTED: 'theme:media:started',
  MEDIA_PAUSED: 'theme:media:paused'
};

// Custom element for deferred media
class DeferredMedia extends HTMLElement {
  #abortController = new AbortController();
  #isPlaying = false;
  #isMuted = false;
  #isLoaded = false;
  #refs = {
    deferredMediaPlayButton: null,
    toggleMediaButton: null,
    toggleSoundButton: null
  };

  // Video service configurations for secure postMessage targeting.
  // Shopify video_url input only accepts YouTube and Vimeo, so we can safely restrict to these origins.
  static videoServices = {
    youtube: {
      urlPattern: 'https://www.youtube.com/embed/',
      origin: 'https://www.youtube.com',
      commands: {
        play: 'playVideo',
        pause: 'pauseVideo',
        mute: 'mute',
        unmute: 'unMute'
      },
      formatMessage: (func, value = '') => JSON.stringify({
        event: 'command',
        func: func,
        args: value
      })
    },
    vimeo: {
      urlPattern: 'https://player.vimeo.com/video/',
      origin: 'https://player.vimeo.com',
      commands: {
        play: 'play',
        pause: 'pause',
        mute: 'setVolume',
        unmute: 'setVolume'
      },
      formatMessage: (method, value) => JSON.stringify(
        value !== undefined ? { method, value } : { method }
      )
    }
  };

  /**
   * Get the video service configuration for a given iframe.
   * @param {HTMLIFrameElement} iframe - The iframe element to check
   * @returns {Object|null} The service config with name and config, or null if not recognized
   */
  #getVideoService(iframe) {
    if (!iframe?.src) return null;

    for (const [name, config] of Object.entries(DeferredMedia.videoServices)) {
      if (iframe.src.startsWith(config.urlPattern)) {
        return { name, config };
      }
    }

    console.warn(`DeferredMedia: Unrecognized video service for iframe: ${iframe.src}`);
    return null;
  }

  /**
   * Send a postMessage to the iframe with the correct origin.
   * @param {HTMLIFrameElement} iframe - The target iframe
   * @param {string} command - The command type (play, pause, mute, unmute)
   * @param {*} value - Optional value for the command
   */
  #sendVideoMessage(iframe, command, value) {
    if (!iframe?.contentWindow) return;

    const service = this.#getVideoService(iframe);
    if (!service) return;

    const func = service.config.commands[command];
    if (!func) {
      console.warn(`DeferredMedia: Unknown command "${command}" for ${service.name}`);
      return;
    }

    const message = service.config.formatMessage(func, value);

    try {
      iframe.contentWindow.postMessage(message, service.config.origin);
    } catch (error) {
      console.error(`DeferredMedia: Error sending message to ${service.name} iframe:`, error);
    }
  }

  connectedCallback() {
    const signal = this.#abortController.signal;

    // Get refs by attribute
    this.#updateRefs();

    // Listen for other media starting (pause this one)
    document.addEventListener(EVENTS.MEDIA_STARTED, this.#handleGlobalMediaStart.bind(this), { signal });

    // Handle play button clicks
    if (this.#refs.deferredMediaPlayButton) {
      this.#refs.deferredMediaPlayButton.addEventListener('click', this.showDeferredMedia.bind(this), { signal });
    }

    // Handle toggle button clicks
    if (this.#refs.toggleMediaButton) {
      this.#refs.toggleMediaButton.addEventListener('click', this.toggleMedia.bind(this), { signal });
    }

    // Handle sound button clicks
    if (this.#refs.toggleSoundButton) {
      this.#refs.toggleSoundButton.addEventListener('click', this.toggleSound.bind(this), { signal });
    }

    // Auto-load if autoplay is set and content not already loaded
    if (this.hasAttribute('autoplay') && !this.hasAttribute('data-media-loaded')) {
      this.#loadContent();
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  muteMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.muted = true;
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'mute', 0);
    }

    this.#isMuted = true;
    this.updateSoundHint(true);
  }

  unmuteMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.muted = false;
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'unmute', 1);
    }

    this.#isMuted = false;
    this.updateSoundHint(false);
  }

  #updateRefs() {
    // Find elements by ref attribute
    const deferredButton = this.querySelector('[ref="deferredMediaPlayButton"]');
    const toggleButton = this.querySelector('[ref="toggleMediaButton"]');
    const soundButton = this.querySelector('[ref="toggleSoundButton"]');

    if (deferredButton) this.#refs.deferredMediaPlayButton = deferredButton;
    if (toggleButton) this.#refs.toggleMediaButton = toggleButton;
    if (soundButton) this.#refs.toggleSoundButton = soundButton;
  }

  /**
   * Check if this is a background video (no user controls).
   * Background videos should not be affected by global media events
   * and should not affect other videos when they play.
   * @returns {boolean} True if this is a background video with no controls
   */
  #isBackgroundVideo() {
    return !this.#refs.toggleMediaButton && !this.#refs.toggleSoundButton;
  }

  showDeferredMedia() {
    this.#loadContent();
    this.#playMedia();
  }

  toggleMedia() {
    if (this.#isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  toggleSound() {
    if (this.#isMuted) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  playMedia() {
    if (!this.#isLoaded) {
      this.#loadContent();
      return;
    }

    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.play().catch(console.warn);
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'play');
    }

    this.#isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  pauseMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.pause();
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'pause');
    }

    this.#isPlaying = false;
    this.updatePlayPauseHint(false);
  }

  updatePlayPauseHint(isPlaying) {
    const toggleButton = this.#refs.toggleMediaButton;
    if (!toggleButton) return;

    toggleButton.classList.remove('hidden');

    const playIcon = toggleButton.querySelector('.icon-play');
    const pauseIcon = toggleButton.querySelector('.icon-pause');

    if (playIcon) {
      playIcon.classList.toggle('hidden', isPlaying);
    }
    if (pauseIcon) {
      pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  updateSoundHint(isMuted) {
    const toggleButton = this.#refs.toggleSoundButton;
    if (!toggleButton) return;

    toggleButton.classList.remove('hidden');

    const mutedIcon = toggleButton.querySelector('.icon-muted');
    const unmutedIcon = toggleButton.querySelector('.icon-unmuted');

    if (mutedIcon) {
      mutedIcon.classList.toggle('hidden', !isMuted);
    }
    if (unmutedIcon) {
      unmutedIcon.classList.toggle('hidden', isMuted);
    }
  }

  #handleGlobalMediaStart(event) {
    // Background videos (no controls) should not be affected by global media events
    if (this.#isBackgroundVideo()) {
      return;
    }

    // Pause this video if another one starts
    if (event.detail.source !== this && this.#isPlaying) {
      this.pauseMedia();
    }
  }

  #loadContent() {
    if (this.#isLoaded) return;

    const template = this.querySelector('template');
    if (!template) return;

    // Only dispatch global event for interactive videos (with controls).
    // Background videos should not pause other videos when they autoplay.
    if (!this.#isBackgroundVideo()) {
      document.dispatchEvent(new CustomEvent(EVENTS.MEDIA_STARTED, {
        detail: { source: this }
      }));
    }

    // Clone and append template content
    const content = template.content.cloneNode(true);
    this.appendChild(content);

    // Mark as loaded
    this.#isLoaded = true;
    this.setAttribute('data-media-loaded', 'true');

    // Hide the initial play button (not the toggle button)
    if (this.#refs.deferredMediaPlayButton) {
      this.#refs.deferredMediaPlayButton.classList.add('hidden');
    }

    // Handle video element loading
    const video = this.querySelector('.video__element');
    if (video) {
      this.#setupVideoElement(video);
      // Sync muted state with the video element after setup
      this.#isMuted = video.muted;
      this.updateSoundHint(video.muted);
    }

    // Handle iframe loading
    const iframe = this.querySelector('.video__iframe');
    if (iframe) {
      this.#setupIframeElement(iframe);
    }
  }

  #setupVideoElement(video) {
    // Ensure Safari/iOS autoplay requirements are met
    if (this.hasAttribute('autoplay')) {
      video.muted = true;
      video.setAttribute('muted', '');
    }
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const onCanPlay = () => {
      video.removeEventListener('canplay', onCanPlay);
      if (video.hasAttribute('autoplay')) {
        video.play().catch(console.warn);
      }
    };

    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('play', () => {
      this.#isPlaying = true;
      this.updatePlayPauseHint(true);
    });
    video.addEventListener('pause', () => {
      this.#isPlaying = false;
      this.updatePlayPauseHint(false);
    });
    video.addEventListener('ended', () => {
      this.#isPlaying = false;
      this.updatePlayPauseHint(false);
    });
  }

  #setupIframeElement(iframe) {
    // For iframes, we assume they start playing immediately if autoplay
    if (this.hasAttribute('autoplay')) {
      this.#isPlaying = true;
      this.updatePlayPauseHint(true);
      // YouTube/Vimeo iframes start muted when autoplay is enabled (browser requirement)
      this.#isMuted = true;
      this.updateSoundHint(true);
    } else {
      // Non-autoplay videos: user clicked to play, so they start with audio enabled
      this.#isMuted = false;
      this.updateSoundHint(false);
    }

    // Listen for messages from YouTube/Vimeo
    // Use abort signal to ensure cleanup when element is disconnected (prevents memory leak)
    window.addEventListener('message', (event) => {
      if (event.source === iframe.contentWindow) {
        this.#handleIframeMessage(event.data);
      }
    }, { signal: this.#abortController.signal });
  }

  #handleIframeMessage(data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;

      // YouTube events
      if (parsed.event === 'onStateChange') {
        // YouTube player states: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
        if (parsed.info === 1) {
          this.#isPlaying = true;
          this.updatePlayPauseHint(true);
        } else if (parsed.info === 2 || parsed.info === 0) {
          this.#isPlaying = false;
          this.updatePlayPauseHint(false);
        }
      }

      // Vimeo events
      if (parsed.event === 'play') {
        this.#isPlaying = true;
        this.updatePlayPauseHint(true);
      } else if (parsed.event === 'pause' || parsed.event === 'ended') {
        this.#isPlaying = false;
        this.updatePlayPauseHint(false);
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }

  #playMedia() {
    if (!this.#isLoaded) {
      this.#loadContent();
      return;
    }

    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.play().catch(console.warn);
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'play');
    }

    this.#isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  // Public API
  play() {
    this.playMedia();
  }

  pause() {
    this.pauseMedia();
  }

  get isPlaying() {
    return this.#isPlaying;
  }

  get isLoaded() {
    return this.#isLoaded;
  }
}

// Register custom element
if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

// Initialize existing elements (for cases where JS loads after HTML)
const initializeExistingElements = () => {
  document.querySelectorAll('deferred-media:not(:defined)').forEach(element => {
    // Force upgrade if not already upgraded
    customElements.upgrade(element);
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeExistingElements);
} else {
  initializeExistingElements();
}

// Handle dynamically added content
document.addEventListener('shopify:section:load', initializeExistingElements);
document.addEventListener('shopify:block:select', initializeExistingElements);
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach(link => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) return;

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }
    this.slider.scrollTo({
      left: this.slideScrollPosition
    });
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach(link => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
    this.play();
  }

  focusInHandling(event) {
    const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
    if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.play();
    } else if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', theme.language_strings.accessibility_play_slideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', theme.language_strings.accessibility_pause_slideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector('.slideshow__slide').clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const button = item.querySelector('a');
      if (index === this.currentPage - 1) {
        if (button) button.removeAttribute('tabindex');
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (button) button.setAttribute('tabindex', '-1');
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options.map((option, index) => {
        return this.options[index] === option;
      }).includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;

    const mediaGallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);

    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    const newMediaModal = modalContent.querySelector( `[data-media-id="${this.currentVariant.featured_media.id}"]`);
    modalContent.prepend(newMediaModal);
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({ }, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton) return;
    shareButton.updateUrl(`${window.theme.shop.url}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html')
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, theme.language_strings.variant_sold_out);
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.theme.language_strings.variant_add_to_cart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.theme.language_strings.variant_unavailable;
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.productData = this.productData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    this.variantData = this.productData?.variants ?? {};
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('variant-radios', VariantRadios);

class BundleSelects extends VariantSelects {
  constructor() {
    super();
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateVariantInput();
      this.renderProductInfo();
    }
  }

  updateVariantInput() {
    const input = this.querySelector('input[name^="bundle_product-"]');
    input.value = this.currentVariant.id;
    // input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('bundle-selects', BundleSelects);

class BundleRadios extends BundleSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

customElements.define('bundle-radios', BundleRadios);

/**
 * Device size check
 */
const deviceIsMobile = () => window.innerWidth <= 768;

/**
 *
 * @param {String} image  - image url
 * @param {Object} param1 - image sizes: {maxWidth: 480, maxHeight: 480}
 * @returns {String} - image url
 */
const imageForSize = (image, {maxWidth, maxHeight})=> {
  const splitUrl = image.split('?');
  const notQuery = splitUrl[0];
  const query = splitUrl[1] ? `?${splitUrl[1]}` : '';

  // Use the section before the query
  const imageTokens = notQuery.split('.');

  // Take the token before the file extension and append the dimensions
  const imagePathIndex = imageTokens.length - 2;

  imageTokens[imagePathIndex] = `${imageTokens[imagePathIndex]}_${maxWidth}x${maxHeight}`;
  return `${imageTokens.join('.')}${query}`;
}

class ScrollLock {
  constructor() {
    this.mobileScrollPosition = 0;
    this.mobileTopLogo = document.querySelector('.dynamic-logo__mobile');
    this.mobileScrollLogo = document.querySelector('.dynamic-logo__change');
    this.preventScrollEvents = false;
    this.scrollEventPreventer = this.scrollEventPreventer.bind(this);
  }

  enabledBodyScrollLock(bodyPos = null) {
    this.mobileScrollPosition = window.pageYOffset;
    let pos = this.mobileScrollPosition;
    if(bodyPos) pos = bodyPos;

    if(pos > 1 && this.mobileTopLogo && this.mobileScrollLogo) {
      this.mobileTopLogo.style.opacity = 0;
      this.mobileScrollLogo.style.opacity = 1;
    }

    // Prevent scroll event from firing when we set position: fixed; on body. We don't want other elements reacting to this "scroll" event
    this.preventScrollEvents = true;
    window.addEventListener('scroll', this.scrollEventPreventer, { capture: true, passive: false });

    // Apply body styles to prevent body scroll interaction
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${pos}px`;
      body.style.width = '100%';
    }
  }

  disableBodyScrollLock() {
    if(this.mobileTopLogo && this.mobileScrollLogo)  {
      this.mobileTopLogo.style.removeProperty('opacity');
      this.mobileScrollLogo.style.removeProperty('opacity');
    }

    const body = document.querySelector('body');
    if (body) ['overflow', 'position', 'top', 'width'].forEach(prop => body.style.removeProperty(prop));

    // Remove scroll event prevention
    this.preventScrollEvents = false;
    window.removeEventListener('scroll', this.scrollEventPreventer, { capture: true, passive: false });

    // Return to where we had scrolled before locking the body
    window.scrollTo({top: this.mobileScrollPosition, left: 0, behavior: 'instant' });
  }

  scrollEventPreventer(event) {
    if (this.preventScrollEvents) {
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  }
}

// expose the scroll locker to the codebase
const scrollLock = new ScrollLock();
window.scrollLock = scrollLock;

// Slide out drawer
class SlideOutDrawer extends HTMLElement {
  constructor() {
    super();
    // default values for all variables so we have a reference.
    this.container = null;
    this.backdrop = null;
    this.closeButtons = null;
    this.isOpen = false;
    this.identifier = '';
    this.fullIdentifier = '';
    this.isInitialized = false;
    this.observer = null;
    this.escapeListener = null;
    this.focusedElementBeforeOpen = null;
    this.animationDuration = 300; // match this to the CSS animation durations
    // not all slide out drawers will have alphabet links or visual filters.
    // for now we will include them here.
    this.alphabetLinks = null;
    this.visualFilters = null;
    this.isMoved = false; // flag to prevent infinite loops during move
  }

  connectedCallback() {
    // if the component is somehow initialized, early exit.
    if (this.isInitialized) return;

    // Check if we need to move to body before initializing
    if (!this.isMoved && this.parentElement !== document.body) {
      this.moveToBody();
      return; // early exit, the moved element will trigger connectedCallback again
    }

    // set our initialized flag
    this.isInitialized = true;
    // initialize the drawer
    this.initializeDrawer();
  }

  moveToBody = () => {
    // Set the moved flag to prevent infinite loops
    this.isMoved = true;

    // Store the current parent for potential cleanup
    const originalParent = this.parentElement;

    // Remove from current position and append to body
    this.remove();
    document.body.appendChild(this);

    // The connectedCallback will be triggered again when appended to body
  }

  initializeDrawer = () => {
    // set up our component elements we really need.
    this.setElements();
    // if any elements are missing, stop the initialization.
    if (!this.hasElements()) return;
    // set variables.
    this.setVariables();
    // set our events.
    this.setEventListeners();
  }

  setElements = () => {
    this.container = this.querySelector('.slide-out-drawer__container');
    this.backdrop = this.querySelector('.slide-out-drawer__backdrop');
    this.closeButtons = this.querySelectorAll('.slide-out-drawer__close');
    // only consider alphabet links that we know have visual filter data associated.
    this.alphabetLinks = this.querySelectorAll('.slide-out-drawer__alphabetical-navigation-item.has-filters');
    this.visualFilters = this.querySelectorAll('.slide-out-drawer__content--visual-filter');
  }

  hasElements = () => {
    // ensure we have our necessary elements. If any are missing let's make an error statement and return false.
    if (!this.container || !this.backdrop || !this.closeButtons) {
      console.error('Slide-out drawer: Required elements not found', {
        container: !!this.container,
        backdrop: !!this.backdrop,
        closeButton: !!this.closeButtons
      });
      return false;
    }
    return true;
  }

  setVariables = () => {
    // set the isOpen flag based on the presence of the open class.
    this.isOpen = this.classList.contains('open');
    // store the identifier.
    this.identifier = this.dataset.identifier;
    this.fullIdentifier = this.getAttribute('id');
  }

  setEventListeners = () => {
    this.addEventListener('facets:initialized', this.handleFacetsInitialized.bind(this));
    // click close button / link? close the component.
    this.closeButtons.forEach(button => {
      button.addEventListener('click', this.close.bind(this));
    });
    // click into the backdrop? close the component.
    this.backdrop.addEventListener('click', this.close.bind(this));
    // Define escape listener (added/removed dynamically when drawer opens/closes)
    this.escapeListener = (e) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    // in the event the component itself is clicked, stop any and all click events.
    this.addEventListener('click', (e) => e.stopPropagation());
    // some drawers may be used for visual filters or some use case that needs quick links for alphabetical navigation
    this.initializeAlphabeticalNavigation();
  }

  initializeAlphabeticalNavigation() {
    // if we have no alphabet links, move on.
    if(this.alphabetLinks.length === 0) return;
    this.alphabetLinks.forEach((link) => {
      link.addEventListener('click', this.handleAlphabetClick.bind(this));
    });
  }

  handleFacetsInitialized(event) {
    if(!this.contains(event.target)) {
      return;
    }
    // sometimes a drawer may hold filters for product grids. When those components fire off, they may re-inject a new facet form.
    // if a new form is injected, this parent component needs to re-inialize with the latest references
    this.initializeDrawer();
  }

  handleAlphabetClick(event) {
    // if the alphabet filter doesn't have filters, ignore the click.
    if(!event.target.classList.contains('has-filters')) return;
    const letter = event.target.dataset.letter;
    const targetFilter = Array.from(this.visualFilters).find(filter => filter.dataset.letter === letter);
    if(!targetFilter) {
      return;
    }
    const contentContainer = this.querySelector('.slide-out-drawer__content');
    if(!contentContainer){
      return;
    }
    const targetPosition = targetFilter.offsetTop - contentContainer.offsetTop;
    contentContainer.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }

  disconnectedCallback = () => {
    // Only perform cleanup if we're not being moved to body
    if (!this.isMoved) {
      scrollLock.disableBodyScrollLock();
      removeTrapFocus();
      // Clean up escape key listener
      document.removeEventListener('keydown', this.escapeListener);
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
    }
    // reset the initialized value.
    this.isInitialized = false;
  }

  open = () => {
    // if the component isn't initialized, or we are already open then early exit.
    if (!this.isInitialized || this.isDrawerOpen()) return;
    // Store the currently focused element.
    this.focusedElementBeforeOpen = document.activeElement;
    // lock the body.
    scrollLock.enabledBodyScrollLock();
    // Add escape key listener only when drawer is open
    document.addEventListener('keydown', this.escapeListener);
    // request animation frame for smoother animation process
    requestAnimationFrame(() => {
      // adjust attributes and flags
      this.classList.add('open');
      this.setAttribute('aria-hidden', 'false');
      this.isOpen = true
      document.body.classList.add(`overflow-hidden`);;
      // trap element focus
      trapFocus(this.container);
      const firstFocusableElement = getFocusableElements(this.container)[0];
      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
      // dispatch our open event
      this.dispatchEvent(new CustomEvent('slide-out-drawer:open', { bubbles: true }));
    });
  }

  close = (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    if (!this.isInitialized || !this.isDrawerOpen()) return;
    scrollLock.disableBodyScrollLock();
    // Remove escape key listener when drawer closes
    document.removeEventListener('keydown', this.escapeListener);
    this.classList.add('closing');
    this.classList.remove('open');

    const handleAnimationEnd = () => {
      this.classList.remove('closing');
      this.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
      removeTrapFocus();
      document.body.classList.remove(`overflow-hidden`);
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
      this.dispatchEvent(new CustomEvent('slide-out-drawer:close', { bubbles: true }));
      this.removeEventListener('animationend', handleAnimationEnd);
    };

    this.addEventListener('animationend', handleAnimationEnd);
  }

  toggle = () => {
    // if we're open, close. If we're closed, open.
    this.isOpen ? this.close() : this.open();
  }

  isDrawerOpen = () => {
    // a simple return to know if the drawer is open or not.
    return this.isOpen;
  }
}

customElements.define('slide-out-drawer', SlideOutDrawer);

// Slide out drawer opener
class SlideOutDrawerOpener extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    // grab the internal button used for click event
    this.button = this.querySelector('button');
    // if we don't have a button, do not attach the event listener
    if (!this.button) {
      console.error('Slide-out drawer opener: Button not found');
      return;
    }
    // grab the drawer identifier related to this opener
    this.drawerId = this.dataset.drawer || "slide-out-drawer-null";
    this.drawer = document.querySelector(`.slide-out-drawer#${this.drawerId}`);
    if (this.drawer) {
      this.drawer.addEventListener('slide-out-drawer:close', () => {
        this.button.setAttribute('aria-expanded', 'false');
      });
      this.drawer.addEventListener('slide-out-drawer:open', () => {
        this.button.setAttribute('aria-expanded', 'true');
      });
    } else {
      console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
    }
    // attach click event to the internal button
    this.button.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick = debounce(() => {
    if (this.drawer && this.drawer instanceof SlideOutDrawer) {
      this.drawer.open();
    } else {
      console.error(`Slide-out drawer opener: Drawer with id "${this.drawerId}" not found or is not a SlideOutDrawer instance`);
    }
  }, 250);

}

customElements.define('slide-out-drawer-opener', SlideOutDrawerOpener);

class PageGateContainer extends HTMLElement {
  #gated_content;
  #page_gate;
  #page_gate_data;
  #unlocked;

  constructor() {
    super();
    this.#page_gate_data = {};
  }

  async connectedCallback() {
    // unlocked is only set in server side render, this means a customer is present that has unlocked the gates
    // if a customer has unlocked the gate, the <page-gate> element won't render, so take the session data keys in the data and set them.
    this.#unlocked = this.classList.contains('unlocked');
    this.#page_gate_data = this.#setPageGateData();
    if(this.#unlocked){
      this.#setLocalStorageKeys();
      this.#setSessionStorageKeys();
    } else {
      this.#initFreshGate();
    }
  }

  #initFreshGate = async () => {
    this.#setLockedContent();
    this.#setEventListeners();
    this.removeAttribute('data-url');
    await this.#injectGate();
  }

  #setSessionStorageKeys = () => {
    if(this.#page_gate_data.gate_session_storage_keys.length === 0) return;
    this.#page_gate_data.gate_session_storage_keys.forEach((session_storage_key) => {
      sessionStorage.setItem(session_storage_key, true);
    });
  }

  #setLocalStorageKeys = () => {
    if(this.#page_gate_data.gate_local_storage_keys.length === 0) return;
    this.#page_gate_data.gate_local_storage_keys.forEach((local_storage_key) => {
      localStorage.setItem(local_storage_key, true);
    });
  }

  #setPageGateData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  #setLockedContent = () => {
    const template = this.querySelector('#gated-content');
    if (!template) {
      console.warn('Gated content template not found');
      return;
    }
    try {
      this.#gated_content = template.content.cloneNode(true);
      template.remove();
    } catch (error) {
      console.error('Error cloning template content:', error);
      return;
    }
  }

  #setEventListeners = () => {
    this.addEventListener('page-gate:unlocked', this.#openGate.bind(this));
  }

  #injectGate = async () => {
    try {
      const response = await fetch(this.#page_gate_data?.gate_url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(html, 'text/html');
      const parsedMainContent = parsedDocument.querySelector('#main-content');
      if (!parsedMainContent) {
        throw new Error('#main-content not found in fetched content');
      }
      const parsedGate = parsedMainContent.querySelector('page-gate');
      if (!parsedGate) {
        throw new Error('page-gate not found in fetched content');
      }
      this.#page_gate = parsedGate;
      this.innerHTML = '';
      this.appendChild(parsedMainContent);
      this.classList.remove('loading');
      this.classList.add('unlocked');
    } catch (error) {
      console.error('Error injecting gate:', error);
      this.classList.add('error');
    }
  }

  #isValidUnlockEvent = (event) => {
    return (
      event.target instanceof PageGate &&
      this.#page_gate &&
      event.target.id === this.#page_gate.id
    );
  }

  #unlockContent = () => {
    if (!this.#gated_content) {
      throw new Error('Unable to find gated content');
    }
    this.#setSessionStorageKeys();
    this.innerHTML = '';
    this.appendChild(this.#gated_content);
  }

  #openGate = (event) => {
    if (!this.#isValidUnlockEvent(event)) return;
    try {
      this.#unlockContent();
    } catch (error) {
      console.error('Error in openGate method:', error);
    }
  }
}

customElements.define('page-gate-container', PageGateContainer);

class PageGate extends HTMLElement {
  #form;
  #password_input;
  #email_input;
  #required_checkboxes
  #submit_button;
  #response_container;
  #gate_data;
  #request_body;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#gate_data = {};
    this.#request_body = {
      properties: {
        payload: {}
      }
    };
    this.#init();
  }

  #init = () => {
    this.#gate_data = this.#setGateData();
    this.#form = this.querySelector('form');
    this.#submit_button = this.#form?.querySelector('button[type="submit"]');
    this.#response_container = this.#form?.querySelector('.response-container');
    // password_input only on password forms
    this.#password_input = this.#form?.querySelector('input[js-gate-password]');
    // email_input only on email forms
    this.#email_input = this.#form?.querySelector('input[js-gate-email]');
    this.#required_checkboxes = this.#form?.querySelectorAll('input[type="checkbox"][js-gate-required-checkbox]');
    // events
    this.#setEventListeners();
    if(this.#gate_data?.type == "email_form_page_gate"){
      this.#setListrakCode();
      this.#setEmailFromURL();
    } else if(this.#gate_data?.type == "password_form_page_gate"){
      this.#setPasswordFromURL();
    }
    this.#checkLocalStorage();
    this.#updateSubmitButtonState();
  }

  #setGateData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  getData = () => {
    return this.#gate_data;
  }

  #setListrakCode = () => {
    // Listrak recommended format for async _ltk object listener
    // See: https://help.listrak.com/en/articles/4820523-javascript-website-integration-installation
    (function(d) {
      if (typeof _ltk == "undefined") {
        if (document.addEventListener) document.addEventListener("ltkAsyncListener", function() {
          _ltk_util.ready(d)
        });
        else {
          e = document.documentElement;
          e.ltkAsyncProperty = 0;
          e.attachEvent("onpropertychange", function(e) {
            if (e.propertyName == "ltkAsyncProperty") {
              _ltk_util.ready(d)
            }
          })
        }
      } else {
        _ltk_util.ready(d)
      }
    })(() => {
      _ltk.Signup.New(`${this.#gate_data?.listrak_subscribe_id}`, `${this.#email_input?.id}`, _ltk.Signup.TYPE.CLICK, `${this.#submit_button?.id}`);
    });
  }

  #setEventListeners = () => {
    this.#form?.addEventListener('submit', this.#handleFormSubmit.bind(this));
    this.#password_input?.addEventListener('input', debounce(this.#handleInputChange.bind(this), 300));
    this.#email_input?.addEventListener('input', debounce(this.#handleInputChange.bind(this), 300));
    this.#required_checkboxes?.forEach(checkbox => {
      checkbox.addEventListener('change', this.#handleInputChange.bind(this));
    });
  }

  #handleInputChange = () => {
    this.#updateSubmitButtonState();
  }

  #areAllRequiredCheckboxesChecked = () => {
    return Array.from(this.#required_checkboxes || []).every(checkbox => checkbox.checked);
  }

  #updateSubmitButtonState = () => {
    if (this.#submit_button) {
      const isInputValid = this.#password_input?.value.trim() || this.#email_input?.value.trim();
      const areCheckboxesValid = this.#areAllRequiredCheckboxesChecked();
      const isFormValid = isInputValid && areCheckboxesValid;

      this.#submit_button.disabled = !isFormValid;
      this.#submit_button.setAttribute('aria-disabled', (!isFormValid).toString());
    }
  }

  #checkLocalStorage = () => {
    const hasLocalStorageKey = this.#gate_data.linked_local_storage_keys.some((linked_local_storage_key) => {
      if (localStorage.getItem(linked_local_storage_key)) {
        return true;
      }
      return false;
    });
    if (hasLocalStorageKey) {
      this.#unlockGate();
    }
  }

  #setPasswordFromURL = () => {
    if (this.#password_input) {
      const urlParams = new URLSearchParams(window.location.search);
      const passwordParam = urlParams.get('password');
      if (passwordParam) {
        this.#password_input.value = passwordParam;
        this.#updateSubmitButtonState();
        this.#submit_button.click();
      }
    }
  }

  #setEmailFromURL = () => {
    if (this.#email_input) {
      const urlParams = new URLSearchParams(window.location.search);
      const emailParam = urlParams.get('email');
      if (emailParam) {
        this.#email_input.value = emailParam;
        this.#updateSubmitButtonState();
      }
    }
  }

  #setRequestData = () => {
    this.#request_body.properties.payload.gate_id = this.#gate_data.id;
    if (this.#password_input) {
      this.#request_body.properties.payload.submission = this.#password_input.value;
    } else if (this.#email_input) {
      this.#request_body.properties.payload.submission = this.#email_input.value;
    }
    const customerIdInput = document.querySelector('.js-customer-id');
    if (customerIdInput) {
      this.#request_body.properties.payload.customer_id = customerIdInput.value;
    }
  }

  #handleFormSubmit = async (event) => {
    event.preventDefault();
    if (this.#submit_button) {
      this.#submit_button.setAttribute('disabled', 'true');
      this.#submit_button.setAttribute('aria-disabled', 'true');
      this.#submit_button.classList.add('loading');
    }
    await this.#triggerTask();
    if (this.#submit_button) {
      this.#updateSubmitButtonState();
      this.#submit_button.classList.remove('loading');
    }
  }

  #triggerTask = async () => {
    if (Object.keys(this.#gate_data).length === 0) {
      console.error('Task data is empty');
      this.#handleResponse({ status: 400, body: 'Task data is empty' });
      return;
    }
    this.#setRequestData();
    try {
      const response = await fetch(`${window.theme.routes.root_url}apps/datajet/task/${this.#gate_data.datajet_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.#gate_data.datajet_token && { 'Token': `${this.#gate_data.datajet_token}` })
        },
        body: JSON.stringify({ ...this.#request_body })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const responseData = await response.json();
      this.#handleResponse({ status: response.status, body: responseData });
    } catch (error) {
      console.error('Error fetching data:', error);
      this.#handleResponse({ status: error.status || 500, body: error.message });
    }
  }

  #handleResponse = (response) => {
    if (!response) {
      console.error('Response is undefined');
      return;
    }
    if (!this.#response_container) {
      console.warn('Response container not found');
    } else {
      if (response.status === 429) {
        this.#response_container.innerHTML = `<p>Rate limit exceeded. Try again later.</p>`;
        return;
      }
      if (response.body && response.body.return_value) {
        this.#response_container.innerHTML = `<pre>${response.body.return_value.message}</pre>`;
      } else {
        console.error('Unexpected response format:', response);
        this.#response_container.innerHTML = `<p>An unexpected error occurred. Please try again.</p>`;
      }
    }
    if (response.body && response.body.return_value && response.body.return_value.message === 'access granted') {
      this.#proceedWithUnlock();
    }
  }

  #proceedWithUnlock = () => {
    try {
      this.#setLocalStorageKeys();
      this.#setSessionStorageKeys();
      this.#unlockGate();
    } catch (error) {
      console.error('Error during unlock process:', error);
    }
  }

  #setLocalStorageKeys = () => {
    if(this.#gate_data.linked_local_storage_keys.length === 0) {
      return;
    }
    try {
      this.#gate_data.linked_local_storage_keys.forEach((local_storage_key) => {
        localStorage.setItem(local_storage_key, 'true');
      });
    } catch (error) {
      console.error('Error setting local storage data:', error);
      throw error; // Re-throw to be caught in #proceedWithUnlock
    }
  }

  #setSessionStorageKeys = () => {
    if(this.#gate_data.linked_session_storage_keys.length === 0) {
      return;
    }
    try {
      this.#gate_data.linked_session_storage_keys.forEach((session_storage_key) => {
        sessionStorage.setItem(session_storage_key, 'true');
      });
    } catch (error) {
      console.error('Error setting session storage data:', error);
      throw error; // Re-throw to be caught in #proceedWithUnlock
    }
  }

  #unlockGate =() => {
    try {
      const event = new CustomEvent('page-gate:unlocked', {
        bubbles: true
      });
      this.dispatchEvent(event);
    } catch (error) {
      console.error('Error in unlockGate:', error);
      throw error; // Re-throw to be caught in #proceedWithUnlock
    }
  }
}

customElements.define('page-gate', PageGate);

class SiteMessageTag extends HTMLElement {
  #data;
  #can_render;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#data = {};
    this.#init();
  }

  #init = () => {
    this.#data = this.#setData();
    // if we are in the theme editor, go no further.
    // when in design mode, the theme editor code will show a message as it is edited.
    if(Shopify.designMode) return;
    this.#can_render = this.#evaluateSessionStorage();
    this.#updateRendering();
  }

  #setData = () => {
    let tempData = {};
    const dataElement = this.querySelector('[js-data]');
    if (dataElement) {
      tempData = JSON.parse(dataElement.textContent);
      dataElement.remove();
    }
    return tempData;
  }

  getData = () => {
    return this.#data;
  }

  #evaluateSessionStorage = () => {
    const requiredKeys = this.#data.required_session_storage_key;
    if (!requiredKeys || requiredKeys.length === 0) {
      return true;
    }
    return requiredKeys.some(key => sessionStorage.getItem(key) !== null);
  }

  #updateRendering = () => {
    if (this.#can_render) {
      this.classList.add('loaded');
    } else {
      this.remove();
    }
  }
}

customElements.define('site-message-tag', SiteMessageTag);

document.addEventListener('keydown', (event) => {
  if(event.keyCode == 27)
  {
    let zoom_container_desktop = document.querySelector('.zoom-container--desktop');
    if(zoom_container_desktop != null)
    {
      zoom_container_desktop.remove();
      document.body.classList.remove("no-scroll");
    }
  }
});

/**
 * ProductGrid Event Router
 *
 * Centralized event routing for ProductGrid components to optimize performance.
 * Uses a registry pattern where ProductGrids register/unregister themselves,
 * eliminating DOM queries on every event and reducing listeners from N to 1 per event type.
 *
 * Performance optimizations:
 * - Lazy initialization: Only sets up listeners when first ProductGrid registers
 * - Registry cache: Avoids DOM queries by maintaining in-memory registry
 * - Early exit: Stops searching once match is found
 * - Auto-cleanup: Removes listeners when no grids remain
 *
 * Event Flow:
 * 1. ProductGrid registers itself with router on init
 * 2. FilterDrawer/PaginationControl dispatches event with identifier in detail
 * 3. Router finds matching ProductGrid from registry (no DOM query)
 * 4. Router calls appropriate handler method on matched ProductGrid
 *
 * @class ProductGridEventRouter
 */
class ProductGridEventRouter {
  constructor() {
    // Registry: Map of gridId -> ProductGrid instance
    this.registry = new Map();
    this.isInitialized = false;
    this.boundHandlers = {};
  }

  /**
   * Register a ProductGrid instance with the router
   * Called by ProductGrid when it initializes
   * @param {ProductGrid} grid - The ProductGrid instance to register
   * @returns {void}
   */
  register(grid) {
    // Ensure grid has required config
    if (!grid || !grid.config || !grid.config.gridId) {
      console.warn('ProductGridEventRouter: Cannot register grid without config.gridId', grid);
      return;
    }

    const gridId = String(grid.config.gridId);
    this.registry.set(gridId, grid);

    // Lazy initialization: Set up listeners when first grid registers
    if (!this.isInitialized) {
      this.init();
    }
  }

  /**
   * Unregister a ProductGrid instance from the router
   * Called by ProductGrid when it disconnects
   * @param {ProductGrid} grid - The ProductGrid instance to unregister
   * @returns {void}
   */
  unregister(grid) {
    if (!grid || !grid.config || !grid.config.gridId) {
      return;
    }

    const gridId = String(grid.config.gridId);
    this.registry.delete(gridId);

    // Auto-cleanup: Remove listeners when no grids remain
    if (this.registry.size === 0 && this.isInitialized) {
      this.cleanup();
    }
  }

  /**
   * Initialize the event router
   * Sets up document-level listeners for all ProductGrid-related events
   * Only called once when first ProductGrid registers
   * @private
   */
  init() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Bind handlers once and store references for cleanup
    this.boundHandlers = {
      filterChange: this.handleFilterChange.bind(this),
      sortChange: this.handleSortChange.bind(this),
      clearFilters: this.handleClearFilters.bind(this),
      facetsFilterChange: this.handleFacetsFilterChange.bind(this),
      paginationChange: this.handlePaginationChange.bind(this)
    };

    // Filter drawer events
    document.addEventListener('filter-drawer:filter-change', this.boundHandlers.filterChange);
    document.addEventListener('filter-drawer:sort-change', this.boundHandlers.sortChange);
    document.addEventListener('filter-drawer:clear-filters', this.boundHandlers.clearFilters);

    // Compatibility event from facets.js
    document.addEventListener('facets:filters-changed', this.boundHandlers.facetsFilterChange);

    // Pagination events
    document.addEventListener('pagination:change', this.boundHandlers.paginationChange);
  }

  /**
   * Cleanup event listeners
   * Called when no ProductGrids remain registered
   * @private
   */
  cleanup() {
    if (!this.isInitialized) return;
    this.isInitialized = false;

    // Remove all event listeners
    document.removeEventListener('filter-drawer:filter-change', this.boundHandlers.filterChange);
    document.removeEventListener('filter-drawer:sort-change', this.boundHandlers.sortChange);
    document.removeEventListener('filter-drawer:clear-filters', this.boundHandlers.clearFilters);
    document.removeEventListener('facets:filters-changed', this.boundHandlers.facetsFilterChange);
    document.removeEventListener('pagination:change', this.boundHandlers.paginationChange);

    // Clear bound handlers
    this.boundHandlers = {};
  }

  /**
   * Find ProductGrid instance that matches the event identifier
   * Uses registry cache instead of DOM queries for O(1) lookup
   * @param {CustomEvent} event - The event containing identifier in detail
   * @returns {ProductGrid|null} Matching ProductGrid or null if not found
   * @private
   */
  findMatchingProductGrid(event) {
    const eventIdentifier = event.detail?.identifier;
    if (!eventIdentifier) {
      return null;
    }

    // Early exit if no grids registered
    if (this.registry.size === 0) {
      return null;
    }

    // Both identifiers are now handleized in Liquid, so we can compare directly
    const identifierLower = String(eventIdentifier).toLowerCase();

    // Iterate registry (typically 1-3 grids, rarely more than 10)
    // Early exit when match found
    for (const [gridId, grid] of this.registry) {
      // Ensure grid is still connected and has config
      if (!grid.isConnected || !grid.config || !grid.config.gridId) {
        // Clean up stale entries
        this.registry.delete(gridId);
        continue;
      }

      const gridIdLower = String(grid.config.gridId).toLowerCase();

      // Match if identifier exactly equals gridId or ends with gridId (with or without separator)
      // This handles both simple identifiers (section ID) and compound identifiers (collection-section-block)
      const matches = identifierLower === gridIdLower ||
                     identifierLower.endsWith(`-${gridIdLower}`) ||
                     identifierLower.endsWith(gridIdLower);

      if (matches) {
        return grid;
      }
    }

    return null;
  }

  /**
   * Handle filter change events from FilterDrawer
   * Routes to the matching ProductGrid's handleFilterChange method
   * @param {CustomEvent} event - filter-drawer:filter-change event
   * @private
   */
  handleFilterChange(event) {
    const grid = this.findMatchingProductGrid(event);
    if (grid && typeof grid.handleFilterChange === 'function') {
      grid.handleFilterChange(event);
    }
  }

  /**
   * Handle sort change events from FilterDrawer
   * Routes to the matching ProductGrid's handleSortChange method
   * @param {CustomEvent} event - filter-drawer:sort-change event
   * @private
   */
  handleSortChange(event) {
    const grid = this.findMatchingProductGrid(event);
    if (grid && typeof grid.handleSortChange === 'function') {
      grid.handleSortChange(event);
    }
  }

  /**
   * Handle clear filters events from FilterDrawer
   * Routes to the matching ProductGrid's handleClearFilters method
   * @param {CustomEvent} event - filter-drawer:clear-filters event
   * @private
   */
  handleClearFilters(event) {
    const grid = this.findMatchingProductGrid(event);
    if (grid && typeof grid.handleClearFilters === 'function') {
      grid.handleClearFilters(event);
    }
  }

  /**
   * Handle filter changes from facets.js (compatibility)
   * Routes to the matching ProductGrid's handleFacetsFilterChange method
   * @param {CustomEvent} event - facets:filters-changed event
   * @private
   */
  handleFacetsFilterChange(event) {
    const grid = this.findMatchingProductGrid(event);
    if (grid && typeof grid.handleFacetsFilterChange === 'function') {
      grid.handleFacetsFilterChange(event);
    }
  }

  /**
   * Handle pagination change events from PaginationControl
   * Routes to the matching ProductGrid's handlePaginationChange method
   * @param {CustomEvent} event - pagination:change event
   * @private
   */
  handlePaginationChange(event) {
    const grid = this.findMatchingProductGrid(event);
    if (grid && typeof grid.handlePaginationChange === 'function') {
      grid.handlePaginationChange(event);
    }
  }

}

// Create router instance (lazy initialization - only sets up listeners when first grid registers)
// No DOM queries or listeners until a ProductGrid registers itself
window.productGridEventRouter = new ProductGridEventRouter();
